/**
 * ThoughtProcess - collapsible "Thought process" disclosure for a chatbot assistant turn.
 *
 * Shows the turn's reasoning-summary text and its ordered tool steps above the answer. While the turn is
 * streaming it is expanded with a live indicator; once the answer completes the renderer collapses it to a
 * one-line summary ("Thought for {Xs} · {N} tools"). Historical turns start collapsed. When the turn has no
 * reasoning and no tool steps there is nothing to disclose, so the component renders nothing.
 *
 * Presentation only — the parent (ChatbotTurnList) owns the open/closed decision (live vs latest vs history)
 * and feeds the data. Assembled from existing pieces (chatbot-thinking-ux, no duplicate components):
 *   - shadcn `Collapsible` for the disclosure,
 *   - the (previously orphaned) `ToolResultCard` for each step,
 *   - `formatDuration` for the elapsed label.
 */
import { useState } from 'react'
import { Brain, Wrench, ChevronRight, Loader2 } from 'lucide-react'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { MarkdownContent } from '@/components/common/MarkdownContent'
import { cn, formatDuration } from '@/lib/utils'
import type { ThoughtStep } from '@/types/chatbot'
import { ToolResultCard } from './ToolResultCard'

export interface ThoughtProcessProps {
  /** Reasoning-summary text for the turn (absent on non-reasoning models). */
  reasoning?: string
  /** Ordered tool steps for the turn. */
  steps?: ThoughtStep[]
  /** Whether the turn is still streaming (expanded + live indicator). */
  isLive?: boolean
  /** Controlled open state. When omitted, the component manages its own (default: `isLive`). */
  open?: boolean
  /** Notified when the user toggles the disclosure (and used to drive auto-collapse from the parent). */
  onOpenChange?: (open: boolean) => void
  /** Elapsed seconds for the "Thought for {Xs}" label. */
  elapsedSeconds?: number
  className?: string
}

/** Build the collapsed summary from whichever clauses are present (omits absent ones). */
function buildSummary(hasReasoning: boolean, toolCount: number, elapsedSeconds?: number): string {
  const clauses: string[] = []
  if (hasReasoning) {
    clauses.push(elapsedSeconds && elapsedSeconds > 0 ? `Thought for ${formatDuration(elapsedSeconds)}` : 'Thought process')
  }
  if (toolCount > 0) {
    clauses.push(`${toolCount} tool${toolCount === 1 ? '' : 's'}`)
  }
  return clauses.join(' · ')
}

export function ThoughtProcess({
  reasoning,
  steps,
  isLive = false,
  open,
  onOpenChange,
  elapsedSeconds,
  className,
}: ThoughtProcessProps) {
  const hasReasoning = !!reasoning?.trim()
  const toolCount = steps?.length ?? 0

  // Nothing to disclose — render nothing (no empty toggle). Callers can mount unconditionally.
  const [internalOpen, setInternalOpen] = useState(isLive)
  if (!hasReasoning && toolCount === 0) {
    return null
  }

  const isControlled = open !== undefined
  const actualOpen = isControlled ? open : internalOpen
  const handleOpenChange = (next: boolean) => {
    if (!isControlled) setInternalOpen(next)
    onOpenChange?.(next)
  }

  const Icon = hasReasoning ? Brain : Wrench
  const summary = isLive ? (hasReasoning ? 'Thinking…' : 'Working…') : buildSummary(hasReasoning, toolCount, elapsedSeconds)

  return (
    <Collapsible
      open={actualOpen}
      onOpenChange={handleOpenChange}
      className={cn('rounded-lg border bg-muted/20', className)}
    >
      <CollapsibleTrigger
        className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-muted-foreground hover:bg-muted/40 transition-colors rounded-lg"
      >
        <ChevronRight className={cn('h-3.5 w-3.5 shrink-0 transition-transform', actualOpen && 'rotate-90')} />
        <Icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground/70" />
        <span className="font-medium">{summary}</span>
        {isLive && <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-muted-foreground/70" aria-hidden />}
      </CollapsibleTrigger>

      <CollapsibleContent>
        <div className="space-y-2 border-t px-3 py-2" aria-label="Assistant thought process">
          {hasReasoning && (
            <div
              className="text-xs italic text-muted-foreground whitespace-pre-wrap leading-relaxed"
              aria-label="Assistant reasoning"
            >
              <MarkdownContent content={reasoning!} variant="default" />
            </div>
          )}

          {toolCount > 0 && (
            <div className="space-y-1.5">
              {steps!.map(step => (
                <ToolResultCard
                  key={step.id}
                  toolName={step.name}
                  isLoading={step.status === 'running'}
                  error={step.status === 'error' ? step.error ?? 'Tool failed' : undefined}
                >
                  {step.resultHint && (
                    <span className="text-xs text-muted-foreground">{step.resultHint}</span>
                  )}
                </ToolResultCard>
              ))}
            </div>
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  )
}
